import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Trophy, Star, Medal } from 'lucide-react';

const AwardsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const awards = [
    {
      id: 1,
      title: 'Best Tech Innovation 2024',
      organization: 'African Tech Awards',
      year: '2024',
      icon: Trophy,
      description: 'Recognized for our groundbreaking AI/ML solutions'
    },
    {
      id: 2,
      title: 'Cybersecurity Excellence',
      organization: 'Security Industry Association',
      year: '2023',
      icon: Medal,
      description: 'Outstanding contribution to cybersecurity advancement'
    },
    {
      id: 3,
      title: 'Innovation Partner',
      organization: 'Sierra Leone Tech Hub',
      year: '2023',
      icon: Star,
      description: 'Leading technology partner for digital transformation'
    },
    {
      id: 4,
      title: 'Client Choice Award',
      organization: 'Enterprise Software Review',
      year: '2024',
      icon: Award,
      description: 'Highest client satisfaction in ERP implementations'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <section id="awards" className="py-20 md:py-24 bg-lion-gray">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.span 
            variants={itemVariants}
            className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
          >
            RECOGNITION & AWARDS
          </motion.span>
          
          <motion.h2 
            variants={itemVariants}
            className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-4"
          >
            Industry Recognition
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="max-w-2xl mx-auto text-gray-600"
          >
            Our commitment to excellence has been recognized by leading industry organizations and clients worldwide.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {awards.map((award, index) => (
            <motion.div
              key={award.id}
              variants={cardVariants}
              custom={index}
              className="bg-white p-6 rounded-lg text-center hover:shadow-xl transition-all duration-300 group"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-lion-blue to-lion-yellow rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <award.icon className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-lg font-poppins font-bold text-lion-blue mb-2 group-hover:text-lion-yellow transition-colors duration-300">
                {award.title}
              </h3>
              
              <p className="text-sm text-gray-600 mb-2">
                {award.organization}
              </p>
              
              <p className="text-xs text-lion-yellow font-semibold mb-3">
                {award.year}
              </p>
              
              <p className="text-sm text-gray-600 leading-relaxed">
                {award.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 text-center"
        >
          <div>
            <div className="text-3xl font-bold text-lion-blue mb-2">50+</div>
            <div className="text-gray-600">Projects Completed</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-lion-blue mb-2">98%</div>
            <div className="text-gray-600">Client Satisfaction</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-lion-blue mb-2">5+</div>
            <div className="text-gray-600">Years Experience</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-lion-blue mb-2">4</div>
            <div className="text-gray-600">Industry Awards</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AwardsSection;